import { createWorker } from '@/core/worker/workerManager';
import { HslClassifier } from '@/core/analysis/modules/classifiers/HslClassifier';
import { MESSAGE_TYPES } from '@/core/analysis/modules/constants';
import { buildHistogramShapeSummary, buildCrossChannelSummary } from '@/core/analysis/modules/HistogramShapeAnalyzer';
import { referencePreferCacheRead, runtimeEnv } from '@/config/featureFlags';

// Bump cache profile to invalidate stale reference core/evidence generated by old luminosity histogram contract.
// Step E.3：升至 v0.9.4.2 以隔离 Step D 之前写入、仍含逐像素 Mask 的旧快照（见 §1.4 E.3/E.5）。
export const REFERENCE_ANALYSIS_PROFILE_VERSION = 'v0.9.4.2';
const PROFILE_VERSION = REFERENCE_ANALYSIS_PROFILE_VERSION;
const CORE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const EVIDENCE_TTL_MS = 3 * 24 * 60 * 60 * 1000;

const memoryCache = new Map();

// 同一 cacheKey 的分析在途 Promise：用于跨调用方（upload 预热、结果页对比）去重，
// 避免并发发起多个相同的 Worker 分析（内存放大 / OOM 根因之一）。
const inFlightReferenceCores = new Map();

const hasGrayscaleData = (value) => {
    return Boolean(
        value &&
        typeof value === 'object' &&
        value.data &&
        Number(value.width) > 0 &&
        Number(value.height) > 0
    );
};

const toUint8Array = (value) => {
    if (!value) return null;
    if (value instanceof Uint8Array) return value;
    if (value instanceof Uint8ClampedArray) return new Uint8Array(value);
    if (Array.isArray(value)) return new Uint8Array(value);
    try {
        return new Uint8Array(value);
    } catch (_error) {
        return null;
    }
};

const computeWorkerAlignedLuminance = (r, g, b) => {
    return Math.round((0.21 * Number(r)) + (0.72 * Number(g)) + (0.07 * Number(b)));
};

const buildGrayscaleFromRgba = (rgba, width, height) => {
    if (!rgba || !width || !height) {
        return null;
    }
    const expectedLength = width * height * 4;
    if (rgba.length !== expectedLength) {
        return null;
    }
    const grayscale = new Uint8Array(width * height);
    for (let i = 0, p = 0; i < rgba.length; i += 4, p += 1) {
        grayscale[p] = computeWorkerAlignedLuminance(rgba[i], rgba[i + 1], rgba[i + 2]);
    }
    return {
        data: grayscale,
        width,
        height
    };
};

const normalizeGrayscalePayload = (payload) => {
    if (!payload || !payload.data) {
        return null;
    }
    const width = Number(payload.width || 0);
    const height = Number(payload.height || 0);
    const data = toUint8Array(payload.data);
    if (!width || !height || !data || data.length !== width * height) {
        return null;
    }
    return { data, width, height };
};

// Step D：取消 25 张预生成 Mask，Worker 不再产出 MASK_METADATA / MASK_CHUNK / LUMINANCE_MASK_* 分块消息，
// 主线程对应的分块重组（createMaskAssembler / buildAssembledMaskMap）随之移除。
// reference 结果中来自「后端/缓存预计算」的 zoneMasks / rgbMasks（evidence 契约）仍由 buildReferenceResult 处理（见 Step E）。

const ensureCoreGrayscaleData = async ({ core, imageStore, cacheKey, imageUrl }) => {
    if (!core || typeof core !== 'object') {
        return core;
    }

    const normalizedCore = { ...core };
    const normalizedExisting = normalizeGrayscalePayload(normalizedCore.grayscaleData);

    // 运行态强制与上传图口径对齐：统一从源图像像素重算 grayscaleData。
    // 只有在无法重算（例如测试/极端异常）时才回退已有值。
    if (!imageStore) {
        normalizedCore.grayscaleData = normalizedExisting;
        return normalizedCore;
    }

    try {
        const { imageData, width, height } = await loadImageDataFromUrl({ imageStore, cacheKey, imageUrl });
        const recomputed = buildGrayscaleFromRgba(imageData, width, height);
        normalizedCore.grayscaleData = recomputed;

        if (isDev()) {
            console.log('debug [ReferenceAnalysisService] recompute core grayscaleData from source image', {
                width,
                height,
                dataLength: recomputed?.data?.length || 0,
                replacedExisting: Boolean(normalizedExisting)
            });
        }
    } catch (error) {
        normalizedCore.grayscaleData = normalizedExisting;
        if (isDev()) {
            console.warn('debug [ReferenceAnalysisService] recompute core grayscaleData failed, fallback existing', {
                message: error?.message || 'unknown_error',
                hasFallback: Boolean(normalizedExisting)
            });
        }
    }

    return normalizedCore;
};

const hasHslPartitionDistributions = (core) => {
    const distributions = core?.hslStatistics?.partitionDistributions;
    return Boolean(distributions && typeof distributions === 'object' && Object.keys(distributions).length > 0);
};

const REQUIRED_CORE_FIELDS = {
    labStatisticsGlobal: (core) => Boolean(core?.labStatistics?.global),
    hslStatisticsGlobal: (core) => Boolean(core?.hslStatistics?.global),
    hslStatisticsPartitions: (core) => hasHslPartitionDistributions(core),
    colorPaletteGlobal: (core) => Boolean(core?.colorPalette?.global),
    labDetailedAnalysis: (core) => Boolean(core?.labDetailedAnalysis),
    hslDetailedAnalysis: (core) => Boolean(core?.hslDetailedAnalysis),
    rgbAnalysis: (core) => Boolean(core?.rgbAnalysis),
    redAnalysis: (core) => Boolean(core?.redAnalysis),
    greenAnalysis: (core) => Boolean(core?.greenAnalysis),
    blueAnalysis: (core) => Boolean(core?.blueAnalysis),
    graynessAnalysis: (core) => Boolean(core?.graynessAnalysis),
    brightnessAnalysis: (core) => Boolean(core?.brightnessAnalysis),
    lightnessAnalysis: (core) => Boolean(core?.lightnessAnalysis),
    luminosityAnalysis: (core) => Boolean(core?.luminosityAnalysis),
    rgbDistribution: (core) => Boolean(core?.rgbDistribution),
    redDistribution: (core) => Boolean(core?.redDistribution),
    greenDistribution: (core) => Boolean(core?.greenDistribution),
    blueDistribution: (core) => Boolean(core?.blueDistribution),
    grayDistribution: (core) => Boolean(core?.grayDistribution),
    luminosityDistribution: (core) => Boolean(core?.luminosityDistribution),
    grayscaleData: (core) => hasGrayscaleData(core?.grayscaleData)
};

const getMissingCoreFields = (core) => {
    return Object.entries(REQUIRED_CORE_FIELDS)
        .filter(([, check]) => !check(core))
        .map(([field]) => field);
};

const isCoreComplete = (core) => {
    if (!core || typeof core !== 'object') {
        return false;
    }

    return getMissingCoreFields(core).length === 0;
};

const COMPARISON_REQUIRED_FIELDS = [
    'rgbDistribution',
    'luminosityDistribution',
    'labStatisticsGlobal',
    'colorPaletteGlobal'
];

const isCoreUsableForComparison = (core) => {
    if (!core || typeof core !== 'object') {
        return false;
    }
    return COMPARISON_REQUIRED_FIELDS.every((field) => REQUIRED_CORE_FIELDS[field]?.(core));
};

const hasHistogramShapeSummary = (core) => {
    return Boolean(
        core?.rgbShapeSummary
        && core?.redShapeSummary
        && core?.greenShapeSummary
        && core?.blueShapeSummary
        && core?.rgbCrossChannelSummary
    );
};

const ensureHistogramShapeSummary = (core) => {
    if (!core || typeof core !== 'object') {
        return core;
    }

    if (hasHistogramShapeSummary(core)) {
        return core;
    }

    const normalizedCore = { ...core };
    const totalPixels = Number(core?.totalPixels ?? core?.imageInfo?.totalPixels ?? 0);
    const hasRgbHistograms = Boolean(
        Array.isArray(core?.rgbAnalysis)
        && Array.isArray(core?.redAnalysis)
        && Array.isArray(core?.greenAnalysis)
        && Array.isArray(core?.blueAnalysis)
    );

    if (!hasRgbHistograms || totalPixels <= 0) {
        return normalizedCore;
    }

    try {
        if (!normalizedCore.rgbShapeSummary) {
            normalizedCore.rgbShapeSummary = buildHistogramShapeSummary({
                histogram: normalizedCore.rgbAnalysis,
                totalPixels,
                channel: 'RGB',
                stepAverages: normalizedCore.rgbDistribution?.span?.stepAverages || null
            });
        }

        if (!normalizedCore.redShapeSummary) {
            normalizedCore.redShapeSummary = buildHistogramShapeSummary({
                histogram: normalizedCore.redAnalysis,
                totalPixels,
                channel: 'R',
                stepAverages: normalizedCore.redDistribution?.span?.stepAverages || null
            });
        }

        if (!normalizedCore.greenShapeSummary) {
            normalizedCore.greenShapeSummary = buildHistogramShapeSummary({
                histogram: normalizedCore.greenAnalysis,
                totalPixels,
                channel: 'G',
                stepAverages: normalizedCore.greenDistribution?.span?.stepAverages || null
            });
        }

        if (!normalizedCore.blueShapeSummary) {
            normalizedCore.blueShapeSummary = buildHistogramShapeSummary({
                histogram: normalizedCore.blueAnalysis,
                totalPixels,
                channel: 'B',
                stepAverages: normalizedCore.blueDistribution?.span?.stepAverages || null
            });
        }

        if (!normalizedCore.rgbCrossChannelSummary) {
            normalizedCore.rgbCrossChannelSummary = buildCrossChannelSummary({
                rHist: normalizedCore.redAnalysis,
                gHist: normalizedCore.greenAnalysis,
                bHist: normalizedCore.blueAnalysis,
                totalPixels
            });
        }
    } catch (error) {
        if (isDev()) {
            console.warn('debug [ReferenceAnalysisService] ensureHistogramShapeSummary failed', {
                message: error?.message || 'unknown_error'
            });
        }
    }

    return normalizedCore;
};

const normalizeReferenceCore = (core) => {
    if (!core || typeof core !== 'object') {
        return null;
    }

    const ensuredCore = ensureHistogramShapeSummary(core);

    return {
        ...ensuredCore,
        rgbAnalysis: ensuredCore.rgbAnalysis ?? null,
        redAnalysis: ensuredCore.redAnalysis ?? null,
        greenAnalysis: ensuredCore.greenAnalysis ?? null,
        blueAnalysis: ensuredCore.blueAnalysis ?? null,
        graynessAnalysis: ensuredCore.graynessAnalysis ?? null,
        brightnessAnalysis: ensuredCore.brightnessAnalysis ?? null,
        lightnessAnalysis: ensuredCore.lightnessAnalysis ?? null,
        luminosityAnalysis: ensuredCore.luminosityAnalysis ?? null,
        rgbDistribution: ensuredCore.rgbDistribution ?? null,
        redDistribution: ensuredCore.redDistribution ?? null,
        greenDistribution: ensuredCore.greenDistribution ?? null,
        blueDistribution: ensuredCore.blueDistribution ?? null,
        grayDistribution: ensuredCore.grayDistribution ?? null,
        luminosityDistribution: ensuredCore.luminosityDistribution ?? null,
        rgbShapeSummary: ensuredCore.rgbShapeSummary ?? null,
        redShapeSummary: ensuredCore.redShapeSummary ?? null,
        greenShapeSummary: ensuredCore.greenShapeSummary ?? null,
        blueShapeSummary: ensuredCore.blueShapeSummary ?? null,
        rgbCrossChannelSummary: ensuredCore.rgbCrossChannelSummary ?? null,
        grayscaleData: ensuredCore.grayscaleData ?? null,
        labStatistics: ensuredCore.labStatistics ?? null,
        hslStatistics: ensuredCore.hslStatistics ?? null,
        labDetailedAnalysis: ensuredCore.labDetailedAnalysis ?? null,
        hslDetailedAnalysis: ensuredCore.hslDetailedAnalysis ?? null,
        colorPalette: ensuredCore.colorPalette ?? null
    };
};

const normalizeReferenceEvidence = (evidence, core, { defaultLoaded = false } = {}) => {
    // §1.4.6 Step E.1：不再把逐像素 zoneMasks/rgbMasks 合并进 core/evidence。
    // Worker 主线程 assembler 重组路径已由 Step D 删除；后端/缓存预计算的 zoneMasks/rgbMasks
    // 即使存在于 evidence 对象，也不再透传到 reference 结果（comparison 算法仅使用
    // 直方图与 zoneAnalysis.proportions 等摘要，不读取逐像素 Mask；§1.4.1 第 394 行）。
    // 保留 hasZoneMasks/hasRgbMasks 标志位用于日志诊断。
    const regionMasks = evidence?.regionMasks ?? core?.segmentationResults?.multiClass?.masks ?? null;
    const grayscaleData = evidence?.grayscaleData ?? core?.grayscaleData ?? null;
    const available = typeof evidence?.available === 'boolean'
        ? evidence.available
        : Boolean(regionMasks || grayscaleData);
    const loaded = typeof evidence?.loaded === 'boolean'
        ? evidence.loaded
        : defaultLoaded;

    return {
        available,
        loaded,
        hasZoneMasks: Boolean(evidence?.zoneMasks ?? core?.zoneAnalysis?.masks ?? null),
        hasRgbMasks: Boolean(evidence?.rgbMasks ?? core?.rgbDistribution?.zoneAnalysis?.masks ?? null),
        regionMasks: regionMasks,
        grayscaleData
    };
};

const isDev = () => {
    return runtimeEnv.isDevelopment;
};

const logCoreEvidenceSummary = ({ tag, presetStyleId, mode, core, evidence }) => {
    if (!isDev()) {
        return;
    }

    console.log(`debug [ReferenceAnalysisService] ${tag}`, {
        presetStyleId,
        mode: mode || null,
        coreComplete: isCoreComplete(core),
        missingCoreFields: getMissingCoreFields(core),
        hasZoneMasks: Boolean(evidence?.zoneMasks),
        hasRgbMasks: Boolean(evidence?.rgbMasks),
        hasRegionMasks: Boolean(evidence?.regionMasks),
        evidenceLoaded: Boolean(evidence?.loaded),
        evidenceAvailable: Boolean(evidence?.available)
    });
};

const logHslHueSummary = ({ tag, core }) => {
    const analysis = core?.hslDetailedAnalysis;
    if (!analysis) {
        return;
    }

    const partitions = ['global', 'blacks', 'shadows', 'midtones', 'highlights', 'whites'];
    partitions.forEach((partitionName) => {
        const entry = partitionName === 'global' ? analysis.global : analysis.partitions?.[partitionName];
        if (!entry?.h?.classification) {
            return;
        }

        const distribution = partitionName === 'global'
            ? core?.hslStatistics?.distribution
            : core?.hslStatistics?.partitionDistributions?.[partitionName];
        const hueDistribution = distribution?.h;
        const process = Array.isArray(hueDistribution) && hueDistribution.length > 0
            ? (() => {
                const buckets = HslClassifier.aggregateHueBuckets(hueDistribution);
                const { entropy, resultantLength, peakCount } = HslClassifier.computeHueDistributionStats(buckets);
                const modeMean = HslClassifier.circularDistance(entry?.h?.statistics?.mode, entry?.h?.statistics?.mean);
                const modeMedian = HslClassifier.circularDistance(entry?.h?.statistics?.mode, entry?.h?.statistics?.median);
                return {
                    entropy: Number(entropy.toFixed(4)),
                    resultantLength: Number(resultantLength.toFixed(4)),
                    peakCount,
                    modeMean: modeMean == null ? null : Number(modeMean.toFixed(2)),
                    modeMedian: modeMedian == null ? null : Number(modeMedian.toFixed(2))
                };
            })()
            : null;
        const dominantProcess = Array.isArray(hueDistribution) && hueDistribution.length > 0
            ? (() => {
                const buckets = HslClassifier.aggregateHueBuckets(hueDistribution);
                const total = buckets.reduce((sum, value) => sum + Math.max(0, Number(value) || 0), 0);
                if (total <= 0) {
                    return {
                        total: 0,
                        ratios: [],
                        significantThreshold: 0.08,
                        minCoverage: 0.65,
                        maxCount: 3,
                        significantIndices: [],
                        selectedIndices: [],
                        coverage: 0
                    };
                }
                const ratios = buckets.map((value, index) => ({
                    tone: HslClassifier.getHueToneByIndex(index),
                    ratio: Number((Math.max(0, Number(value) || 0) / total).toFixed(4))
                }));
                const options = HslClassifier.getDominantHueOptions();
                const sorted = ratios
                    .map((item, index) => ({ index, ratio: item.ratio }))
                    .sort((a, b) => b.ratio - a.ratio);
                const significant = sorted.filter((bucket) => bucket.ratio >= options.significantThreshold);
                const selected = (significant.length > 0 ? significant : [sorted[0]]).slice(0, options.maxCount);
                let coverage = selected.reduce((sum, bucket) => sum + bucket.ratio, 0);
                if (significant.length > 0 && coverage < options.minCoverage) {
                    sorted.forEach((bucket) => {
                        if (coverage >= options.minCoverage || selected.length >= options.maxCount) return;
                        if (!selected.some((item) => item.index === bucket.index)) {
                            selected.push(bucket);
                            coverage += bucket.ratio;
                        }
                    });
                }
                return {
                    total: Number(total.toFixed(2)),
                    ratios,
                    significantThreshold: options.significantThreshold,
                    minCoverage: options.minCoverage,
                    maxCount: options.maxCount,
                    significantIndices: significant.map((item) => item.index),
                    selectedIndices: selected.map((item) => item.index),
                    coverage: Number(coverage.toFixed(4))
                };
            })()
            : null;

        console.log(`[ReferenceAnalysisService] dominantHues ${tag}`, {
            partition: partitionName,
            dominantHues: entry?.h?.classification?.dominantHues || [],
            isAchromatic: Boolean(entry?.h?.classification?.isAchromatic),
            distribution: entry?.h?.classification?.distribution || null,
            stability: entry?.h?.classification?.stability || null,
            process,
            dominantProcess
        });
    });
};

const toDataUrl = async ({ imageStore, cacheKey, imageUrl }) => {
    console.log('[ReferenceAnalysisService] toDataUrl 开始', {
        cacheKey: cacheKey?.substring(0, 80) + '...',
        imageUrl: imageUrl?.substring(0, 50) + '...',
        hasImageStore: !!imageStore,
        hasGetReferenceImageCache: !!imageStore?.getReferenceImageCache
    });

    if (cacheKey && imageStore?.getReferenceImageCache) {
        const startRead = performance.now();
        const cachedImage = await imageStore.getReferenceImageCache(cacheKey);
        const readDuration = Math.round(performance.now() - startRead);

        console.log('[ReferenceAnalysisService] IndexedDB 图片缓存读取结果', {
            cacheKey: cacheKey?.substring(0, 80) + '...',
            hasCache: !!cachedImage,
            hasData: !!cachedImage?.data,
            isValidDataUrl: typeof cachedImage?.data === 'string' && cachedImage?.data?.startsWith('data:'),
            dataType: typeof cachedImage?.data,
            dataLength: cachedImage?.data?.length || 0,
            timestamp: cachedImage?.timestamp || null,
            readDurationMs: readDuration
        });

        if (typeof cachedImage?.data === 'string' && cachedImage.data.startsWith('data:')) {
            console.log('[ReferenceAnalysisService] 图片缓存命中，使用 data URL', {
                cacheKey: cacheKey?.substring(0, 80) + '...'
            });
            return cachedImage.data;
        }
        console.log('[ReferenceAnalysisService] 图片缓存未命中或无效，准备 fetch', { cacheKey: cacheKey?.substring(0, 80) + '...' });
    } else {
        console.log('[ReferenceAnalysisService] 无法读取缓存（缺少 cacheKey 或 imageStore.getReferenceImageCache）', {
            hasCacheKey: !!cacheKey,
            hasImageStore: !!imageStore,
            hasGetReferenceImageCache: !!imageStore?.getReferenceImageCache
        });
    }

    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    let isSameOrigin = false;

    try {
        if (currentOrigin) {
            isSameOrigin = new URL(imageUrl, currentOrigin).origin === currentOrigin;
        }
    } catch (_error) {
        isSameOrigin = false;
    }

    console.log('[ReferenceAnalysisService] 开始 fetch 图片', { imageUrl: imageUrl?.substring(0, 50) + '...', isSameOrigin });
    const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'omit'
    });
    if (!response.ok) {
        console.warn('[ReferenceAnalysisService] reference image fetch failed', {
            imageUrl,
            status: response.status,
            isSameOrigin,
            credentialsMode: 'omit'
        });
        throw new Error(`reference_image_load_failed_${response.status}`);
    }

    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('reference_image_load_failed'));
        reader.readAsDataURL(blob);
    });

    console.log('[ReferenceAnalysisService] 图片 fetch 完成，准备写入缓存', {
        imageUrl: imageUrl?.substring(0, 50) + '...',
        blobType: blob.type,
        blobSize: blob.size,
        dataUrlLength: dataUrl?.length || 0,
        willSaveToCache: !!(cacheKey && imageStore?.saveReferenceImageCache)
    });

    if (cacheKey && imageStore?.saveReferenceImageCache) {
        const startWrite = performance.now();
        await imageStore.saveReferenceImageCache(cacheKey, {
            data: dataUrl,
            type: blob.type || 'image/png'
        });
        const writeDuration = Math.round(performance.now() - startWrite);
        console.log('[ReferenceAnalysisService] 图片缓存写入完成', {
            cacheKey: cacheKey?.substring(0, 80) + '...',
            writeDurationMs: writeDuration,
            dataUrlLength: dataUrl?.length || 0
        });
    }

    return dataUrl;
};

const loadImageDataFromUrl = async ({ imageStore, cacheKey, imageUrl }) => {
    const dataUrl = await toDataUrl({ imageStore, cacheKey, imageUrl });
    return await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            const context = canvas.getContext('2d');
            context.drawImage(image, 0, 0);
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            resolve({
                imageData: imageData.data,
                width: canvas.width,
                height: canvas.height
            });
        };
        image.onerror = () => reject(new Error('reference_image_load_failed'));
        image.src = dataUrl;
    });
};

const analyzeByWorker = async ({ imageStore, cacheKey, imageUrl }) => {
    let worker;
    try {
        const { imageData, width, height } = await loadImageDataFromUrl({ imageStore, cacheKey, imageUrl });
        worker = await createWorker({ purpose: 'reference-analysis' });
        const collectedPartials = {
            labStatistics: null,
            hslStatistics: null,
            labDetailedAnalysis: null,
            hslDetailedAnalysis: null,
            colorPalette: null,
            grayscaleData: null
        };

        return await new Promise((resolve, reject) => {
            const nonFatalWorkerErrors = [];
            const timeoutId = setTimeout(() => {
                cleanup();
                worker.terminate();
                reject(new Error('reference_analysis_timeout'));
            }, 30000);

            const cleanup = () => {
                clearTimeout(timeoutId);
                worker.removeEventListener('message', handleMessage);
                worker.removeEventListener('error', handleError);
            };

            const handleMessage = (event) => {
                if (event.data.status === 'error') {
                    // Worker 内部会对某些子步骤（如 masks/hsl/lab 分支）上报 error，
                    // 但后续仍可能产出 analysisResults。仅对“无 type 的全局错误”立即失败。
                    if (event.data.type) {
                        nonFatalWorkerErrors.push({
                            type: event.data.type,
                            error: event.data.error || 'worker_step_error'
                        });
                        console.warn('[ReferenceAnalysisService] worker non-fatal step error', {
                            type: event.data.type,
                            error: event.data.error
                        });
                        return;
                    }

                    cleanup();
                    reject(new Error(event.data.error || 'reference_analysis_worker_error'));
                    return;
                }

                if (event.data.status === 'partial' && event.data.analysisType === 'original') {
                    if (event.data.type === MESSAGE_TYPES.LAB_STATISTICS) {
                        collectedPartials.labStatistics = event.data.data || null;
                    } else if (event.data.type === MESSAGE_TYPES.HSL_STATISTICS) {
                        collectedPartials.hslStatistics = event.data.data || null;
                    } else if (event.data.type === MESSAGE_TYPES.LAB_DETAILED_ANALYSIS) {
                        collectedPartials.labDetailedAnalysis = event.data.data || null;
                    } else if (event.data.type === MESSAGE_TYPES.HSL_DETAILED_ANALYSIS) {
                        collectedPartials.hslDetailedAnalysis = event.data.data || null;
                    } else if (event.data.type === MESSAGE_TYPES.COLOR_PALETTE) {
                        collectedPartials.colorPalette = event.data.data || null;
                    } else if (event.data.type === MESSAGE_TYPES.GRAYSCALE_DATA) {
                        const payload = event.data.data || null;
                        if (payload?.data && payload?.width && payload?.height) {
                            collectedPartials.grayscaleData = {
                                data: payload.data instanceof Uint8Array ? payload.data : new Uint8Array(payload.data),
                                width: Number(payload.width),
                                height: Number(payload.height)
                            };
                        }
                        if (isDev()) {
                            console.log('debug [ReferenceAnalysisService] worker partial grayscaleData', {
                                hasData: Boolean(payload?.data),
                                width: Number(payload?.width || 0),
                                height: Number(payload?.height || 0),
                                dataLength: payload?.data?.length || 0
                            });
                        }
                    } // 关闭 GRAYSCALE_DATA 分支
                    // Step D：Worker 不再产出 MASK_* / LUMINANCE_MASK_* 分块消息，对应主线程重组分支已移除。
                }

                if (event.data.status === 'partial' && event.data.type === MESSAGE_TYPES.ANALYSIS_RESULTS) {
                    cleanup();
                    const originalCore = event.data.data?.original || null;
                    if (!originalCore) {
                        if (nonFatalWorkerErrors.length > 0) {
                            console.warn('[ReferenceAnalysisService] worker finished without original core', {
                                nonFatalWorkerErrors
                            });
                        }
                        resolve(null);
                        return;
                    }

                    // Step D：Worker 不再产出分块 Mask，main-thread 重组已移除，直接采用 ANALYSIS_RESULTS 的 original core。
                    const mergedOriginalCore = originalCore;

                    resolve({
                        ...mergedOriginalCore,
                        labStatistics: mergedOriginalCore.labStatistics || collectedPartials.labStatistics || null,
                        hslStatistics: mergedOriginalCore.hslStatistics || collectedPartials.hslStatistics || null,
                        labDetailedAnalysis: mergedOriginalCore.labDetailedAnalysis || collectedPartials.labDetailedAnalysis || null,
                        hslDetailedAnalysis: mergedOriginalCore.hslDetailedAnalysis || collectedPartials.hslDetailedAnalysis || null,
                        colorPalette: mergedOriginalCore.colorPalette || collectedPartials.colorPalette || null,
                        grayscaleData: mergedOriginalCore.grayscaleData || collectedPartials.grayscaleData || null
                    });
                }
            };

            const handleError = (errorEvent) => {
                const detail = errorEvent?.message
                    || errorEvent?.error?.message
                    || 'unknown_worker_error';
                console.error('[ReferenceAnalysisService] worker runtime error', {
                    message: errorEvent?.message,
                    filename: errorEvent?.filename,
                    lineno: errorEvent?.lineno,
                    colno: errorEvent?.colno
                });
                cleanup();
                reject(new Error(`reference_analysis_worker_error:${detail}`));
            };

            worker.addEventListener('message', handleMessage);
            worker.addEventListener('error', handleError);

            worker.postMessage({
                imageData,
                width,
                height
            });
        });
    } finally {
        if (worker) {
            worker.terminate();
        }
    }
};

const buildCacheKey = ({ presetStyleId, imageUrl, profile = 'core', version = PROFILE_VERSION }) => {
    return `${presetStyleId}::${imageUrl}::${profile}::${version}`;
};

const isExpired = (timestamp, ttl) => {
    if (!timestamp) {
        return true;
    }
    return (Date.now() - Number(timestamp)) > ttl;
};

const normalizeCoreResult = ({ presetStyleId, imageUrl, mode, core, analyzedAt = Date.now(), evidence = null }) => {
    const normalizedCore = normalizeReferenceCore(core);
    // §1.4.6 Step E.1：不再把逐像素 zoneMasks/rgbMasks 合并进 evidence 对象。
    // 保留 grayscaleData（§1.4.2 决策：grayscale 不在本次取消范围）。
    const evidenceAlignedWithCore = {
        ...(evidence || {}),
        grayscaleData: normalizedCore?.grayscaleData ?? evidence?.grayscaleData ?? null
    };
    const normalizedEvidence = normalizeReferenceEvidence(evidenceAlignedWithCore, normalizedCore, { defaultLoaded: false });

    return {
        presetStyleId,
        profileVersion: PROFILE_VERSION,
        source: {
            mode,
            imageUrl,
            analyzedAt
        },
        core: normalizedCore,
        evidence: normalizedEvidence
    };
};

const normalizeCachedResult = ({ result, profile }) => {
    if (!result) {
        return null;
    }

    if (profile === 'evidence') {
        return normalizeReferenceEvidence(result, null, { defaultLoaded: true });
    }

    return normalizeCoreResult({
        presetStyleId: result?.presetStyleId,
        imageUrl: result?.source?.imageUrl,
        mode: result?.source?.mode,
        core: result?.core,
        analyzedAt: result?.source?.analyzedAt,
        evidence: result?.evidence
    });
};

const isCacheUsable = (profile, result) => {
    if (profile !== 'core') {
        return Boolean(result);
    }

    return isCoreComplete(result?.core);
};

const buildStaleResult = (profile, result) => {
    if (!result) {
        return null;
    }

    if (profile === 'evidence') {
        return {
            ...result,
            stale: true
        };
    }

    return {
        ...result,
        source: {
            ...(result?.source || {}),
            stale: true
        }
    };
};

const shouldTryStaleFallback = (error) => {
    const message = String(error?.message || '');
    return (
        message.includes('reference_image_load_failed_401')
        || message.includes('reference_image_load_failed_403')
        || message.includes('preset_analysis_http_401')
        || message.includes('preset_analysis_http_403')
    );
};

const tryLoadFromCache = async ({ imageStore, cacheKey, profile = 'core', allowStale = false }) => {
    const memoryHit = memoryCache.get(cacheKey);
    const ttl = profile === 'core' ? CORE_TTL_MS : EVIDENCE_TTL_MS;
    let staleCandidate = null;

    if (memoryHit) {
        const normalizedMemoryResult = normalizeCachedResult({ result: memoryHit.result, profile });
        if (!isCacheUsable(profile, normalizedMemoryResult)) {
            if (isDev()) {
                console.warn('debug [ReferenceAnalysisService] skip invalid cache in memory', { cacheKey, profile });
            }
        } else {
            if (!isExpired(memoryHit.timestamp, ttl)) {
                console.log('[ReferenceAnalysisService] memory cache hit', { cacheKey, profile });
                return normalizedMemoryResult;
            }
            staleCandidate = normalizedMemoryResult;
        }
    }

    if (!imageStore?.getReferenceAnalysisCache) {
        return allowStale ? buildStaleResult(profile, staleCandidate) : null;
    }

    const idbRecord = await imageStore.getReferenceAnalysisCache(profile, cacheKey);
    if (!idbRecord) {
        return allowStale ? buildStaleResult(profile, staleCandidate) : null;
    }

    const normalizedIdbResult = normalizeCachedResult({ result: idbRecord.result, profile });
    if (!isCacheUsable(profile, normalizedIdbResult)) {
        if (isDev()) {
            console.warn('debug [ReferenceAnalysisService] skip invalid cache in idb', { cacheKey, profile });
        }
        return allowStale ? buildStaleResult(profile, staleCandidate) : null;
    }

    if (isExpired(idbRecord.timestamp, ttl)) {
        const staleResult = staleCandidate || normalizedIdbResult;
        return allowStale ? buildStaleResult(profile, staleResult) : null;
    }

    memoryCache.set(cacheKey, {
        ...idbRecord,
        result: normalizedIdbResult
    });
    console.log('[ReferenceAnalysisService] idb cache set');
    return normalizedIdbResult;
};

const writeCache = async ({ imageStore, cacheKey, result, profile = 'core', presetStyleId, imageUrl }) => {
    const payload = {
        cacheKey,
        presetStyleId,
        imageUrl,
        profile,
        result,
        version: PROFILE_VERSION,
        timestamp: Date.now()
    };

    memoryCache.set(cacheKey, payload);

    if (imageStore?.saveReferenceAnalysisCache) {
        await imageStore.saveReferenceAnalysisCache(profile, payload);
    }
};

const runtimeDeps = {
    analyzeByWorker
};

export const ReferenceAnalysisService = {
    buildCacheKey,

    async getReferenceCore({ imageStore, presetStyleId, imageUrl, forceRefresh = false }) {
        if (!presetStyleId || !imageUrl) {
            throw new Error('reference_context_invalid');
        }

        if (isDev()) {
            console.log('[ReferenceAnalysisService] getReferenceCore start', {
                presetStyleId,
                forceRefresh
            });
        }

        const cacheKey = buildCacheKey({ presetStyleId, imageUrl, profile: 'core' });

        if (!forceRefresh && referencePreferCacheRead) {
            const cached = await tryLoadFromCache({ imageStore, cacheKey, profile: 'core' });
            if (cached) {
                if (isDev()) {
                    console.log('[ReferenceAnalysisService] getReferenceCore cache hit', {
                        presetStyleId,
                        sourceMode: cached?.source?.mode
                    });
                }
                logHslHueSummary({
                    tag: 'cache_hit',
                    core: cached?.core
                });
                return cached;
            }
        }

        // 在途去重：同一 cacheKey 的分析正在进行中，直接复用同一个 Promise，
        // 使 upload 预热与结果页对比（或任意并发调用）不会产生重复 Worker 分析。
        const existingInFlight = inFlightReferenceCores.get(cacheKey);
        if (existingInFlight) {
            return existingInFlight;
        }

        const tryRecoverByStaleCache = async (error) => {
            if (forceRefresh || !shouldTryStaleFallback(error)) {
                return null;
            }

            const staleCached = await tryLoadFromCache({
                imageStore,
                cacheKey,
                profile: 'core',
                allowStale: true
            });

            if (!staleCached) {
                return null;
            }

            console.warn('[ReferenceAnalysisService] 使用过期参考缓存降级返回', {
                presetStyleId,
                reason: error?.message || 'unknown_error'
            });
            logHslHueSummary({
                tag: 'stale_cache_fallback',
                core: staleCached?.core
            });
            return staleCached;
        };

        const runReferenceCoreAnalysis = async () => {
            let workerResultRaw;
            try {
                workerResultRaw = await runtimeDeps.analyzeByWorker({
                    imageStore,
                    cacheKey,
                    imageUrl
                });
            } catch (error) {
                const staleRecovered = await tryRecoverByStaleCache(error);
                if (staleRecovered) {
                    return staleRecovered;
                }
                throw error;
            }
            const workerResult = await ensureCoreGrayscaleData({
                core: normalizeReferenceCore(workerResultRaw),
                imageStore,
                cacheKey,
                imageUrl
            });
            if (!workerResult || !isCoreComplete(workerResult)) {
                const missingFields = getMissingCoreFields(workerResult || {});
                if (!isCoreUsableForComparison(workerResult)) {
                    throw new Error(`reference_analysis_worker_error:missing_core_fields:${missingFields.join(',') || 'all'}`);
                }
                console.warn('[ReferenceAnalysisService] worker 结果非完整但可用于 comparison', {
                    presetStyleId,
                    missingFields
                });
            }

            const result = normalizeCoreResult({
                presetStyleId,
                imageUrl,
                mode: 'frontend_computed',
                core: workerResult
            });
            await writeCache({ imageStore, cacheKey, result, profile: 'core', presetStyleId, imageUrl });
            logCoreEvidenceSummary({
                tag: 'getReferenceCore worker fallback success',
                presetStyleId,
                mode: 'frontend_computed',
                core: result?.core,
                evidence: result?.evidence
            });
            logHslHueSummary({
                tag: 'frontend_computed',
                core: result?.core
            });
            return result;
        };

        const analysisPromise = runReferenceCoreAnalysis();
        inFlightReferenceCores.set(cacheKey, analysisPromise);
        try {
            return await analysisPromise;
        } finally {
            inFlightReferenceCores.delete(cacheKey);
        }
    },

    async getReferenceEvidence({ imageStore, presetStyleId, imageUrl, forceRefresh = false }) {
        if (!presetStyleId || !imageUrl) {
            throw new Error('reference_context_invalid');
        }

        if (isDev()) {
            console.log('debug [ReferenceAnalysisService] getReferenceEvidence start', {
                presetStyleId,
                forceRefresh
            });
        }

        const evidenceCacheKey = buildCacheKey({ presetStyleId, imageUrl, profile: 'evidence' });

        if (!forceRefresh && referencePreferCacheRead) {
            const cachedEvidence = await tryLoadFromCache({ imageStore, cacheKey: evidenceCacheKey, profile: 'evidence' });
            if (cachedEvidence) {
                if (isDev()) {
                    console.log('debug [ReferenceAnalysisService] getReferenceEvidence cache hit', {
                        presetStyleId,
                        hasZoneMasks: Boolean(cachedEvidence?.zoneMasks),
                        hasRgbMasks: Boolean(cachedEvidence?.rgbMasks)
                    });
                }
                return cachedEvidence;
            }
        }

        const coreResult = await this.getReferenceCore({
            imageStore,
            presetStyleId,
            imageUrl,
            forceRefresh
        });

        const evidence = normalizeReferenceEvidence(coreResult?.evidence, coreResult?.core, { defaultLoaded: true });

        await writeCache({
            imageStore,
            cacheKey: evidenceCacheKey,
            result: evidence,
            profile: 'evidence',
            presetStyleId,
            imageUrl
        });

        if (isDev()) {
            console.log('debug [ReferenceAnalysisService] getReferenceEvidence built from core', {
                presetStyleId,
                hasZoneMasks: Boolean(evidence?.hasZoneMasks),
                hasRgbMasks: Boolean(evidence?.hasRgbMasks),
                hasRegionMasks: Boolean(evidence?.regionMasks)
            });
        }

        return evidence;
    },

    /**
     * 确保指定 reference 图片已缓存并以 data URL 形式返回。
     * 与 result 分析流程写穿口径完全一致（复用私有 toDataUrl）：
     * - 命中 IndexedDB 缓存 → 直接返回 data URL（零网络）
     * - 未命中 → fetch(mode: cors, credentials: omit) → dataURL → 写回 IndexedDB → 返回 dataURL
     * - CORS/网络失败 → throw（由调用方 catch，不影响展示）
     *
     * 设计目的：history 缩略图展示路径与缓存路径解耦。展示 <img> 无 crossorigin 永不被 CORS 卡住；
     * 本方法作为 best-effort 写穿入口，供 history 在 cache miss 时后台预热缓存。
     *
     * @param {{ imageStore: any, presetStyleId: string, imageUrl: string }} args
     * @returns {Promise<string>} data URL
     */
    async ensureReferenceImageCached({ imageStore, presetStyleId, imageUrl }) {
        if (!presetStyleId || !imageUrl) {
            throw new Error('reference_context_invalid');
        }

        const cacheKey = buildCacheKey({ presetStyleId, imageUrl, profile: 'core' });
        console.log('[ReferenceAnalysisService] ensureReferenceImageCached start', {
            presetStyleId,
            imageUrl: imageUrl?.substring(0, 50) + '...'
        });

        try {
            return await toDataUrl({ imageStore, cacheKey, imageUrl });
        } catch (error) {
            // 固定前缀 debug 日志：辅助定位 CDN 边缘缓存污染（Vary: Origin 缺失）等"偶尔失败"场景
            console.warn('[ReferenceAnalysisService] ensureReferenceImageCached failed', {
                presetStyleId,
                imageUrl,
                errorName: error?.name || 'UnknownError',
                errorMessage: error?.message || String(error)
            });
            throw error;
        }
    }
};

export {
    normalizeReferenceCore,
    normalizeReferenceEvidence,
    isCoreComplete,
    getMissingCoreFields,
    runtimeDeps as referenceAnalysisServiceRuntimeDeps
};

export default ReferenceAnalysisService;
